import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getTestOrdersSearchUrl} from 'sample-tests/ui/utils/testOrderSearchUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';

import {PMEITestOrderQuery, PMEITestOrderQueryVariables} from './__generated/PMEITestOrderQuery';

export type TestOrderOrInaccessibleNode = ExcludeInaccessible<
  PMEITestOrderQuery['procedureMethodExecutionInstances'][number]
>['testOrders'][number];

export type TestOrderNode = ExcludeInaccessible<TestOrderOrInaccessibleNode>;

export type PMEINode = ExcludeInaccessible<PMEITestOrderQuery['procedureMethodExecutionInstances'][number]>;

export type PMEIWithStudyNode = PMEINode & {
  procedureRun: ExcludeInaccessible<PMEINode['procedureRun']> & {
    study: NonNullable<ExcludeInaccessible<PMEINode['procedureRun']['study']>>;
  };
};

interface WorksheetTestOrderUpdatesOnRetractionBannerProps {
  worksheetId: string;
}

export const PMEI_TEST_ORDER_QUERY = gql`
  query PMEITestOrderQuery($worksheetId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    procedureMethodExecutionInstances(worksheetId: $worksheetId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        testOrders {
          ... on TestOrderNode {
            id
            name
            status
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        createdRetestOrders {
          ... on TestOrderNode {
            id
            name
            status
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        procedureRun {
          ... on ProcedureRunNode {
            id
            study {
              ... on StudyNode {
                id
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function WorksheetTestOrderUpdatesOnRetractionBanner(
  props: WorksheetTestOrderUpdatesOnRetractionBannerProps
): JSX.Element | null {
  const pmeiResult = useBenchlingQuery<PMEITestOrderQuery, PMEITestOrderQueryVariables>(
    PMEI_TEST_ORDER_QUERY,
    {
      variables: {worksheetId: props.worksheetId},
      fetchPolicy: 'network-only',
    }
  );

  if (pmeiResult.type !== QueryResultType.SUCCESS) {
    return null;
  }

  // The user should have read access to the PMEIs + study if they can get to this modal, so this is for safety + type narrowing
  pmeiResult.data.procedureMethodExecutionInstances.forEach((pmei) => {
    assert(
      !isInaccessible(pmei) && pmei.procedureRun.study != null && !isInaccessible(pmei.procedureRun.study),
      'Access Denied.'
    );
  });

  return (
    <PresentationalWorksheetTestOrderUpdatesOnRetractionBanner
      pmeis={pmeiResult.data.procedureMethodExecutionInstances as Array<PMEIWithStudyNode>}
    />
  );
}

function inaccessibleCount(orders: Array<TestOrderOrInaccessibleNode>): number {
  return orders.filter((order) => isInaccessible(order)).length;
}

function orderCountPhrase(total: number, inaccessible: number, label: string): string {
  const prefix = inaccessible > 0 ? '(up to) ' : '';
  const disclaimer =
    inaccessible > 0
      ? ` (including ${inaccessible} ${pluralize(inaccessible, label)} that you do not have permission to view)`
      : '';
  return `${prefix}${total} ${pluralize(total, label)}${disclaimer}`;
}

export function PresentationalWorksheetTestOrderUpdatesOnRetractionBanner({
  pmeis,
}: {
  pmeis: Array<PMEIWithStudyNode>;
}): JSX.Element | null {
  const studyId = pmeis[0].procedureRun.study.id;
  assert(
    pmeis.every((pmei) => pmei.procedureRun.study.id === studyId),
    'Expected all PMEIs to have the same study'
  );
  const completedOrInvalidTestOrders = pmeis
    .flatMap((pmei) => pmei.testOrders)
    .filter(
      (to) =>
        isInaccessible(to) || to.status === TestOrderStatus.COMPLETED || to.status === TestOrderStatus.INVALID
    );
  const pendingOrBlockedRetestOrders = pmeis
    .flatMap((pmei) => pmei.createdRetestOrders)
    .filter(
      (to) =>
        isInaccessible(to) || to.status === TestOrderStatus.PENDING || to.status === TestOrderStatus.BLOCKED
    );
  if (completedOrInvalidTestOrders.length === 0 && pendingOrBlockedRetestOrders.length === 0) {
    return null;
  }

  const inaccessibleTestOrderCount = inaccessibleCount(completedOrInvalidTestOrders);
  const inaccessibleRetestOrderCount = inaccessibleCount(pendingOrBlockedRetestOrders);

  return (
    <div className={css(styles.banners)}>
      {completedOrInvalidTestOrders.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'View test orders',
            actionHref: getTestOrdersSearchUrl(completedOrInvalidTestOrders, studyId, [
              TestOrderStatus.COMPLETED,
              TestOrderStatus.INVALID,
            ]),
          }}
          text="Plated test orders that are completed or invalid will be updated."
          supportingInfo={
            <div>
              Cancelling reviews and approvals will update{' '}
              {orderCountPhrase(
                completedOrInvalidTestOrders.length,
                inaccessibleTestOrderCount,
                'test order'
              )}{' '}
              to in progress and clear {completedOrInvalidTestOrders.length === 1 ? 'its' : 'their'} results
              and analysis comments
            </div>
          }
        />
      )}
      {pendingOrBlockedRetestOrders.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'View retest orders',
            actionHref: getTestOrdersSearchUrl(pendingOrBlockedRetestOrders, studyId, [
              TestOrderStatus.PENDING,
              TestOrderStatus.BLOCKED,
            ]),
          }}
          text="Retest orders that are pending or blocked will be cancelled."
          supportingInfo={
            <div>
              Cancelling reviews and approvals will cancel{' '}
              {orderCountPhrase(
                pendingOrBlockedRetestOrders.length,
                inaccessibleRetestOrderCount,
                'retest order'
              )}
            </div>
          }
        />
      )}
    </div>
  );
}

export default WorksheetTestOrderUpdatesOnRetractionBanner;

const styles = StyleSheet.create({
  banners: {
    paddingBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});
